import type { DashboardContract, Locale } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { cn } from "@chams-pay/design-system/lib/utils";
import { ChevronRight, CreditCard, UserRound } from "lucide-react";
import { useRef } from "react";
import { arrearsStudentFilters, type StudentFilters } from "../core/use-students.js";
import type { DashboardController } from "../core/use-operations.js";
import {
  AcademicCollectionChart,
  AcademicYearSelector,
  ActivityCollectionsChart,
  CollectionGauge,
} from "../ui/dashboard-charts.js";
import { Amount, Bidi, Count, UnavailableFigure } from "../ui/figures.js";
import { TabbarSpacer } from "./shell.js";

type MessageKey = Parameters<typeof translate>[1];

function Inset({
  className,
  children,
}: {
  readonly className?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className={cn("rounded-2xl bg-card shadow-[0_1px_3px_rgb(16_28_30/0.1)]", className)}>
      {children}
    </div>
  );
}

function collectionScopeLabel(scope: DashboardContract["collectionScope"], locale: Locale): string {
  if (scope.kind === "academic-year") return scope.academicYear;
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn", {
    month: "long",
    year: "numeric",
  }).format(new Date(`${scope.month}-01T12:00:00Z`));
}

function MobileSkeleton() {
  return (
    <div className="flex flex-col gap-3.5 p-3.5" aria-hidden="true">
      <Inset className="flex items-center gap-4 p-[18px]">
        <div className="chams-skeleton size-[88px] shrink-0 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <div className="chams-skeleton h-2 w-[55%]" />
          <div className="chams-skeleton h-4 w-[75%]" />
          <div className="chams-skeleton h-[22px] w-[45%] rounded-full" />
        </div>
      </Inset>
      <div className="grid grid-cols-2 gap-3">
        {[0, 1].map((index) => (
          <Inset key={index} className="flex flex-col gap-2.5 p-[15px]">
            <div className="chams-skeleton h-[7px] w-3/5" />
            <div className="chams-skeleton h-5 w-4/5" />
            <div className="chams-skeleton h-[7px] w-[70%]" />
          </Inset>
        ))}
      </div>
      <Inset className="overflow-hidden">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className="flex min-h-14 items-center gap-3 border-b border-sunken px-4 last:border-0"
          >
            <div className="chams-skeleton size-2.5 rounded-sm" />
            <div className="chams-skeleton h-2 w-2/5" />
            <div className="chams-skeleton ms-auto h-3.5 w-6" />
          </div>
        ))}
      </Inset>
    </div>
  );
}

export function MobileDashboardOffline({
  locale,
  lastSyncLabel,
  pendingCount,
  readinessTier,
  onTakePayment,
  onOpenStudents,
}: {
  readonly locale: Locale;
  readonly lastSyncLabel: string;
  readonly pendingCount: number;
  readonly readinessTier: "ok" | "warning" | "blocked";
  readonly onTakePayment: () => void;
  readonly onOpenStudents: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  return (
    <section
      className="operations-screen flex flex-col gap-3.5 p-3.5"
      data-testid="dashboard-screen"
    >
      <Inset className="flex items-start gap-3 p-3.5">
        <span
          aria-hidden="true"
          className="grid size-[22px] shrink-0 place-items-center rounded-full bg-sunken font-heading text-xs font-extrabold text-muted-foreground"
        >
          i
        </span>
        <span className="flex flex-col gap-1">
          <strong className="font-heading text-[13px] leading-snug">
            {t("dash.offlineTitle")}
          </strong>
          <span className="text-[11.5px] leading-relaxed text-muted-foreground">
            {t("dash.offlineBody")}
          </span>
        </span>
      </Inset>

      <Inset className="overflow-hidden">
        <div className="flex min-h-14 items-center justify-between border-b border-sunken px-4 text-[12.5px] text-muted-foreground">
          {t("dash.pendingPayments")}
          <Count value={pendingCount} className="text-base font-extrabold text-foreground" />
        </div>
        <div className="flex min-h-14 items-center justify-between border-b border-sunken px-4 text-[12.5px] text-muted-foreground">
          {t("dash.lastSync")}
          <Bidi className="text-[12.5px] font-semibold">{lastSyncLabel}</Bidi>
        </div>
        <div className="flex min-h-14 items-center justify-between px-4 text-[12.5px] text-muted-foreground">
          {t("dash.captureReadiness")}
          <span
            className={cn(
              "inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 font-heading text-[11px] font-bold",
              readinessTier === "ok" && "bg-success-soft text-success",
              readinessTier === "warning" && "bg-warning-soft text-warning",
              readinessTier === "blocked" && "bg-destructive-soft text-destructive",
            )}
          >
            <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
            {t(readinessTier === "blocked" ? "dash.blocked" : "dash.ok")}
          </span>
        </div>
      </Inset>

      <Inset className="flex flex-col gap-2.5 p-4">
        <span className="text-[11.5px] leading-relaxed text-muted-foreground">
          {t("dash.offlineActions")}
        </span>
        <button
          type="button"
          onClick={onTakePayment}
          className="chams-focusable min-h-[50px] rounded-xl bg-primary font-heading text-sm font-bold text-white"
        >
          {t("dash.takePayment")}
        </button>
        <button
          type="button"
          onClick={onOpenStudents}
          className="chams-focusable min-h-12 rounded-xl border border-input bg-card font-heading text-[13px] font-semibold text-muted-foreground"
        >
          {t("dash.nav2")}
        </button>
      </Inset>
      <TabbarSpacer />
    </section>
  );
}

export function MobileDashboardScreen({
  locale,
  controller,
  blocked,
  activityPeriodSelector,
  onOpenStudents,
  onOpenInstruments,
}: {
  readonly locale: Locale;
  readonly controller: DashboardController;
  readonly blocked: boolean;
  readonly activityPeriodSelector: React.ReactNode;
  readonly onOpenStudents: (filters?: StudentFilters) => void;
  readonly onOpenInstruments: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const { dashboard } = controller;
  const seen = useRef(false);
  if (dashboard) seen.current = true;

  if (!dashboard && !controller.failed && !seen.current) {
    return (
      <section className="operations-screen" data-testid="dashboard-screen" aria-busy="true">
        <MobileSkeleton />
        <TabbarSpacer />
      </section>
    );
  }

  const failureNotice = controller.failed && (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-2xl bg-destructive-soft p-3.5 text-destructive"
    >
      <strong className="font-heading text-[13px]">{t("dash.failedTitle")}</strong>
      <span className="text-[11.5px] leading-relaxed">{t(controller.failureBodyKey)}</span>
      <button
        type="button"
        onClick={() => void controller.reload()}
        className="chams-focusable min-h-11 self-start rounded-xl border border-destructive-border bg-card px-4 font-heading text-[12px] font-bold"
      >
        {t("dash.retry")}
      </button>
    </div>
  );

  if (!dashboard) {
    return (
      <section
        className="operations-screen flex flex-col gap-3.5 p-3.5"
        data-testid="dashboard-screen"
      >
        {failureNotice}
        <div className="grid grid-cols-2 gap-3">
          {(["dash.collectionRate", "dash.netIncome"] as const).map((key) => (
            <Inset key={key} className="flex flex-col gap-2 p-[15px]">
              <span className="chams-eyebrow text-[9px]">{t(key)}</span>
              <span className="text-start">
                <UnavailableFigure className="text-[22px] font-extrabold" />
              </span>
              <span className="text-[10px] text-muted-2">{t("dash.unavailable")}</span>
            </Inset>
          ))}
        </div>
        <TabbarSpacer />
      </section>
    );
  }

  const scopeLabel = collectionScopeLabel(dashboard.collectionScope, locale);
  const collectionTitle = t("dash.collectionRate");
  const arrearsRows = [
    {
      key: "one",
      filter: "one" as const,
      label: t("dash.oneMonth"),
      students: dashboard.arrears.oneMonthStudents,
      amountMad: dashboard.arrears.oneMonthMad,
      square: "bg-teal-300",
      row: "",
      figure: "text-foreground",
    },
    {
      key: "two",
      filter: "two" as const,
      label: t("dash.twoMonths"),
      students: dashboard.arrears.twoMonthStudents,
      amountMad: dashboard.arrears.twoMonthMad,
      square: "bg-warning",
      row: "",
      figure: "text-foreground",
    },
    {
      key: "three",
      filter: "three-plus" as const,
      label: t("dash.threePlus"),
      students: dashboard.arrears.threePlusMonthStudents,
      amountMad: dashboard.arrears.threePlusMonthMad,
      square: "bg-destructive",
      row: "bg-[#fdf9f9]",
      figure: "text-destructive",
    },
  ];
  const pendingCount = dashboard.pending.chequeCount + dashboard.pending.transferCount;

  return (
    <section
      className={cn(
        "operations-screen transition-opacity duration-[140ms]",
        controller.busy ? "opacity-60" : "opacity-100",
      )}
      data-testid="dashboard-screen"
      aria-busy={controller.busy}
    >
      <div className={cn("flex flex-col gap-3.5 p-3.5", blocked && "opacity-55")}>
        {failureNotice}

        <Inset className="flex items-center gap-4 p-[18px]">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="chams-eyebrow text-[9px]">
              {collectionTitle} · <span className="capitalize">{scopeLabel}</span>
            </span>
            <span className="text-start">
              <Amount
                value={dashboard.collection.settledAllocationsMad}
                locale={locale}
                className="text-[22px] font-extrabold leading-none"
              />
            </span>
            <span className="flex items-baseline gap-1.5 text-[10px] font-semibold text-muted-foreground">
              {t("dash.expected")}
              <Amount value={dashboard.collection.netObligationsMad} locale={locale} />
            </span>
          </div>
          <CollectionGauge
            percentage={dashboard.collection.percentage}
            label={`${collectionTitle} — ${scopeLabel}`}
            compact
          />
        </Inset>

        <div className="grid grid-cols-2 gap-3">
          <Inset className="flex flex-col gap-2 p-[15px]">
            <span className="chams-eyebrow text-[9px]">
              {t("dash.netIncome")} · {t("dash.monthToDate")}
            </span>
            <span className="text-start">
              <Amount
                value={dashboard.income.netMad}
                locale={locale}
                className="text-[22px] font-extrabold leading-none"
              />
            </span>
            <span className="text-[10px] font-semibold text-muted-foreground">
              <Amount value={dashboard.income.grossMad} locale={locale} /> −{" "}
              <Amount
                value={dashboard.income.cancellationsMad}
                locale={locale}
                tone="destructive"
              />
            </span>
          </Inset>
          <button
            type="button"
            onClick={() => onOpenStudents(arrearsStudentFilters("overdue", dashboard.currentDate))}
            className="chams-focusable flex flex-col gap-2 rounded-2xl border-s-[3px] border-warning bg-card p-[15px] text-start shadow-[0_1px_3px_rgb(16_28_30/0.1)]"
          >
            <span className="chams-eyebrow text-[9px] text-warning">
              {t("dash.overdueReceivables")}
            </span>
            <span className="text-start">
              <Amount
                value={dashboard.overdue.amountMad}
                locale={locale}
                className="text-[22px] font-extrabold leading-none"
              />
            </span>
            <span className="text-[10px] font-semibold text-muted-foreground">
              <Count value={dashboard.overdue.students} /> {t("dash.studentsWord")}
            </span>
          </button>
        </div>
      </div>

      <div className="px-3.5 pb-1.5">
        <div className="chams-eyebrow mb-2 px-1.5">{t("dash.arrearsByMonths")}</div>
        <Inset className="overflow-hidden">
          {arrearsRows.map((row, index) => (
            <button
              key={row.key}
              type="button"
              onClick={() =>
                onOpenStudents(arrearsStudentFilters(row.filter, dashboard.currentDate))
              }
              className={cn(
                "chams-focusable flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-start",
                index < arrearsRows.length - 1 && "border-b border-sunken",
                row.row,
              )}
            >
              <span aria-hidden="true" className={cn("size-2.5 shrink-0 rounded-sm", row.square)} />
              <span className="font-heading text-[13px] font-bold">{row.label}</span>
              <span className="ms-auto flex shrink-0 flex-col items-end gap-0.5">
                <Count
                  value={row.students}
                  className={cn("text-[17px] font-extrabold", row.figure)}
                />
                <Amount
                  value={row.amountMad}
                  locale={locale}
                  className="text-[9.5px] font-bold text-muted-2"
                />
              </span>
              <ChevronRight
                aria-hidden="true"
                className="size-4 shrink-0 text-muted-2 rtl:-scale-x-100"
                strokeWidth={1.9}
              />
            </button>
          ))}
        </Inset>
      </div>

      <div className="px-3.5 pb-1.5 pt-3.5">
        <div className="chams-eyebrow mb-2 px-1.5">{t("dash.activityCollections")}</div>
        <Inset className="p-[16px_14px_12px]">
          <ActivityCollectionsChart
            activity={dashboard.activity}
            period={dashboard.period}
            locale={locale}
            label={t("dash.activityCollections")}
            compact
          />
          {activityPeriodSelector}
        </Inset>
      </div>

      <div className="px-3.5 pb-1.5 pt-3.5">
        <div className="chams-eyebrow mb-2 px-1.5">{t("dash.academicCollection")}</div>
        <Inset className="p-[16px_14px_12px]">
          <AcademicCollectionChart
            collection={dashboard.academicCollection}
            locale={locale}
            label={t("dash.academicCollection")}
            compact
          />
          <AcademicYearSelector
            collection={dashboard.academicCollection}
            locale={locale}
            onChange={controller.setAcademicYear}
            compact
          />
        </Inset>
      </div>

      <div className="px-3.5 pb-5 pt-3.5">
        <div className="chams-eyebrow mb-2 px-1.5">{t("dash.attentionQueue")}</div>
        <Inset className="overflow-hidden">
          <button
            type="button"
            onClick={onOpenInstruments}
            className="chams-focusable flex min-h-[60px] w-full items-center gap-3 border-b border-sunken px-4 py-2.5 text-start"
          >
            <span className="grid size-[34px] shrink-0 place-items-center rounded-[10px] bg-warning-soft">
              <CreditCard aria-hidden="true" className="size-4 text-warning" strokeWidth={1.75} />
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="font-heading text-[13px] font-bold">
                {t("dash.pendingInstruments")}
              </span>
              <Amount
                value={BigInt(dashboard.pending.chequeMad) + BigInt(dashboard.pending.transferMad)}
                locale={locale}
                className="text-[11px] font-normal text-muted-2"
              />
            </span>
            <span className="ms-auto shrink-0">
              <Count value={pendingCount} className="text-[17px] font-extrabold" />
            </span>
            <ChevronRight
              aria-hidden="true"
              className="size-4 shrink-0 text-muted-2 rtl:-scale-x-100"
              strokeWidth={1.9}
            />
          </button>
          <button
            type="button"
            onClick={() =>
              onOpenStudents(
                arrearsStudentFilters(
                  "registration",
                  dashboard.currentDate,
                  dashboard.currentAcademicYear,
                ),
              )
            }
            className="chams-focusable flex min-h-[60px] w-full items-center gap-3 px-4 py-2.5 text-start"
          >
            <span className="grid size-[34px] shrink-0 place-items-center rounded-[10px] bg-primary-soft">
              <UserRound aria-hidden="true" className="size-4 text-primary" strokeWidth={1.75} />
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="font-heading text-[13px] font-bold">
                {t("dash.lateRegistrations")}
              </span>
              <Amount
                value={dashboard.overdueRegistration.amountMad}
                locale={locale}
                className="text-[11px] font-normal text-muted-2"
              />
            </span>
            <span className="ms-auto shrink-0">
              <Count
                value={dashboard.overdueRegistration.students}
                className="text-[17px] font-extrabold"
              />
            </span>
            <ChevronRight
              aria-hidden="true"
              className="size-4 shrink-0 text-muted-2 rtl:-scale-x-100"
              strokeWidth={1.9}
            />
          </button>
        </Inset>
      </div>

      {blocked && (
        <div className="mx-3.5 mb-5 flex flex-col gap-2.5 rounded-2xl border border-dashed border-destructive-border bg-card p-3.5">
          <button
            type="button"
            disabled
            className="min-h-[50px] cursor-not-allowed rounded-xl bg-[#e7d8d7] font-heading text-sm font-bold text-white"
          >
            {t("dash.takePayment")}
          </button>
          <span className="text-[11.5px] leading-relaxed text-destructive">
            {t("dash.blockedHint")}
          </span>
        </div>
      )}

      <TabbarSpacer />
    </section>
  );
}
